import { Injectable } from '@angular/core';
import { AppState } from '@app/store';
import { select, Store } from '@ngrx/store';
import { setSmartRecoFormValues, setSmartRecoPathologies } from './actions/smart-reco-patient.actions';
import { Pathology } from '@app/core/models/pathology';
import {
  getSmartRecoCatalogProducts,
  getSmartRecoNotRecommendedProduct,
  getSmartRecoNutritionalAssessment,
  getSmartRecoRecommendation,
  goToRationing,
  incrementRetryRecommendationCount,
  removeSmartRecoNotRecommendedProduct,
  resetSmartRecoProductRecommendationError,
  setIsSelectedIndividualisProduct,
  setSmartRecoDryProduct,
  setSmartRecoTreat,
  setSmartRecoWetProduct,
} from './actions/smart-reco-recommendation.actions';
import { PetInfo, Product, ProductRecommendation, ProductRecommendationErrorTypes } from '@app/core/models';
import { navigateBackToStep, resetSmartRecoState, setSmartRecoStep, startSmartReco } from './actions/smart-reco-steps.actions';
import { SmartRecoStep } from '../smart-reco-step';
import { PanelSectionTypeEnum } from '@app/shared/utils';
import {
  selectNutritionalAssessmentBenefitClaim,
  selectNutritionalAssessmentHasError,
  selectNutritionalAssessmentLoading,
  selectNutritionalAssessmentMacronutrients,
  selectNutritionalAssessmentOtherNutrients,
} from './selectors/smart-reco-nutritional-assessment.selectors';
import { BenefitClaim, Nutrient } from '@app/shared/components/nutrients-list/nutrients';
import { Observable } from 'rxjs';
import { selectPathologies } from './selectors/smart-reco-patient.selectors';
import {
  selectIsIndividualisSelected,
  selectRecoHasIndividualis,
  selectRecoProducts,
  selectIsRecoAccurate,
  selectRecoProductsCostPerDay,
  selectRecoNotRecommendedCostPerDay,
  selectRecoCatalogProductsAutocomplete,
  selectRecoNotRecommendedProduct,
  selectRecoProductRecommendationErrorType,
  selectRecoProductRecommendationRetryCount,
  selectRecoProductRecommendationSessionIdError,
  selectRelatedWetProducts,
  selectRelatedTreats,
} from './selectors/smart-reco-recommendation.selectors';
import { SystemPreferenceCode } from '@app/shared/utils/enums/system-preference-code';
import { selectLastStep, selectSmartRecoPetSummary, selectStep, selectSteps } from './selectors/smart-reco-steps.selectors';

@Injectable()
export class SmartRecoFacade {
  /** smart-reco-nutritional-assessment.selectors */
  public nutritionalAssessmentMacronutrients$: Observable<Nutrient[]> = this.store$.pipe(select(selectNutritionalAssessmentMacronutrients));
  public nutritionalAssessmentOtherNutrients$: Observable<Nutrient[]> = this.store$.pipe(select(selectNutritionalAssessmentOtherNutrients));
  public nutritionalAssessmentBenefitClaim$: Observable<BenefitClaim[]> = this.store$.pipe(select(selectNutritionalAssessmentBenefitClaim));
  public nutritionalAssessmentHasError$: Observable<boolean> = this.store$.pipe(select(selectNutritionalAssessmentHasError));
  public nutritionalAssessmentLoading$: Observable<boolean> = this.store$.pipe(select(selectNutritionalAssessmentLoading));

  /** smart-reco-patient.selectors */
  public pathologies$: Observable<Pathology[]> = this.store$.pipe(select(selectPathologies));

  /** smart-reco-recommendation.selectors */
  public recoHasIndividualis$ = (currentSystemPreferenceCode: SystemPreferenceCode): Observable<boolean> =>
    this.store$.select(selectRecoHasIndividualis(currentSystemPreferenceCode));
  public recoProducts$: Observable<ProductRecommendation[]> = this.store$.pipe(select(selectRecoProducts));
  public isIndividualisSelected$: Observable<boolean> = this.store$.pipe(select(selectIsIndividualisSelected));
  public isRecoAccurate$: Observable<boolean> = this.store$.pipe(select(selectIsRecoAccurate));
  public recoProductsCostPerDay$: Observable<number[]> = this.store$.pipe(select(selectRecoProductsCostPerDay));
  public recoNotRecommendedCostPerDay$: Observable<number> = this.store$.pipe(select(selectRecoNotRecommendedCostPerDay));
  public recoCatalogProductsAutocomplete$: Observable<
    {
      label: string;
      value: Product;
    }[]
  > = this.store$.pipe(select(selectRecoCatalogProductsAutocomplete));
  public recoNotRecommendedProduct$: Observable<ProductRecommendation> = this.store$.pipe(select(selectRecoNotRecommendedProduct));
  public recoProductRecommendationErrorType$: Observable<ProductRecommendationErrorTypes> = this.store$.pipe(
    select(selectRecoProductRecommendationErrorType),
  );
  public recoProductRecommendationRetryCount$: Observable<number> = this.store$.pipe(select(selectRecoProductRecommendationRetryCount));
  public recoProductRecommendationSessionIdError$: Observable<string> = this.store$.pipe(
    select(selectRecoProductRecommendationSessionIdError),
  );
  public relatedWetProducts$ = (dryProduct: Product): Observable<Product[]> => this.store$.select(selectRelatedWetProducts(dryProduct));
  public relatedTreats$ = (dryProduct: Product): Observable<Product[]> => this.store$.select(selectRelatedTreats(dryProduct));

  /** smart-reco-steps.selectors */
  public steps$: Observable<SmartRecoStep[]> = this.store$.pipe(select(selectSteps));
  public step$: Observable<SmartRecoStep> = this.store$.pipe(select(selectStep));
  public lastStep$: Observable<SmartRecoStep> = this.store$.pipe(select(selectLastStep));
  public smartRecoPetSummary$: Observable<PetInfo> = this.store$.pipe(select(selectSmartRecoPetSummary));

  constructor(private readonly store$: Store<AppState>) {}

  /** smart-reco-patient.actions */
  setSmartRecoFormValues(): void {
    this.store$.dispatch(setSmartRecoFormValues());
  }

  setSmartRecoPathologies(pathologies: Pathology[]): void {
    this.store$.dispatch(setSmartRecoPathologies({ value: pathologies }));
  }

  /** smart-reco-recommendation.actions */
  getSmartRecoRecommendation(): void {
    this.store$.dispatch(getSmartRecoRecommendation());
  }

  incrementRetryRecommendationCount(): void {
    this.store$.dispatch(incrementRetryRecommendationCount());
  }

  getSmartRecoNutritionalAssessment(): void {
    this.store$.dispatch(getSmartRecoNutritionalAssessment());
  }

  getSmartRecoCatalogProducts(): void {
    this.store$.dispatch(getSmartRecoCatalogProducts());
  }

  getSmartRecoNotRecommendedProduct(product: Product): void {
    this.store$.dispatch(getSmartRecoNotRecommendedProduct({ product }));
  }

  removeSmartRecoNotRecommendedProduct(): void {
    this.store$.dispatch(removeSmartRecoNotRecommendedProduct());
  }

  setSmartRecoDryProduct(product: Product, packId?: string, fromTour?: boolean, isUnselect?: boolean): void {
    this.store$.dispatch(setSmartRecoDryProduct({ product, packId, fromTour, isUnselect }));
  }

  goToRationing(): void {
    this.store$.dispatch(goToRationing());
  }

  setSmartRecoWetProduct(product: Product, packId?: string): void {
    this.store$.dispatch(setSmartRecoWetProduct({ product, packId }));
  }

  setSmartRecoTreat(product: Product, packId?: string): void {
    this.store$.dispatch(setSmartRecoTreat({ product, packId }));
  }

  resetSmartRecoProductRecommendationError(): void {
    this.store$.dispatch(resetSmartRecoProductRecommendationError());
  }

  setIsSelectedIndividualisProduct(select: boolean): void {
    this.store$.dispatch(setIsSelectedIndividualisProduct({ select }));
  }

  /** smart-reco-steps.actions */
  startSmartReco(withIdentification: boolean, isNewPet?: boolean): void {
    this.store$.dispatch(startSmartReco({ withIdentification, isNewPet }));
  }

  setSmartRecoStep(step: SmartRecoStep): void {
    this.store$.dispatch(setSmartRecoStep({ value: step }));
  }

  navigateBackToStep(value: PanelSectionTypeEnum): void {
    this.store$.dispatch(navigateBackToStep({ value }));
  }

  resetSmartRecoState(): void {
    this.store$.dispatch(resetSmartRecoState());
  }
}
